import { useSetAtom } from "jotai";
import { SearchIcon } from "lucide-react";
import { Link, useMatch } from "react-router-dom";
import { CreditMeter } from "@/components/billing/credit-meter";
import { Logo } from "@/components/brand/logo";
import { useLocalInboxCounts } from "@/components/overview/use-local-count";
import { Shortcut } from "@/components/ui/kbd";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useCurrentProject } from "@/hooks/use-current-project";
import { cn } from "@/lib/utils";
import {
	type NavItem,
	projectNavigation,
	projectNavSections,
	projectPath,
	projectSettingsNavItem,
	workspaceNavigation,
} from "@/routes/app-navigation";
import { commandPaletteOpenAtom } from "@/store/ui";
import { ProjectSwitcher } from "./project-switcher";
import { SyncIndicator } from "./sync-indicator";
import { UserMenu } from "./user-menu";

function SidebarLink({
	item,
	to,
	end = false,
	badge,
}: {
	item: NavItem;
	to: string;
	end?: boolean;
	badge?: number;
}) {
	const Icon = item.icon;
	const active = !!useMatch({ path: to, end });
	return (
		<Tooltip>
			<TooltipTrigger asChild>
				<Link
					to={to}
					aria-current={active ? "page" : undefined}
					onPointerEnter={() => void item.page.preload()}
					onFocus={() => void item.page.preload()}
					className={cn(
						"flex h-8 items-center gap-2.5 rounded-md px-2 text-[13px] text-sidebar-foreground outline-none transition-colors duration-75 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 focus-visible:ring-ring/30",
						active && "bg-sidebar-accent font-medium text-sidebar-accent-foreground",
					)}
				>
					<Icon className="size-4 shrink-0 opacity-80" />
					<span className="truncate">{item.label}</span>
					{!!badge && (
						<span className="ml-auto rounded-sm bg-foreground px-1 font-mono text-[10.5px] leading-4 font-medium text-background tabular-nums">
							{badge > 99 ? "99+" : badge}
						</span>
					)}
				</Link>
			</TooltipTrigger>
			<TooltipContent side="right">
				{item.label}
				{!!badge && ` · ${badge} waiting`}
				<Shortcut
					keys={item.shortcut}
					className="[&_kbd]:border-background/20 [&_kbd]:bg-background/10 [&_kbd]:text-background"
				/>
			</TooltipContent>
		</Tooltip>
	);
}

export function AppSidebar() {
	const { projectId, project } = useCurrentProject();
	const openPalette = useSetAtom(commandPaletteOpenAtom);
	const inbox = useLocalInboxCounts(projectId);
	const badges: Record<string, number> = { inbox: inbox.approvals + inbox.comments };

	return (
		<aside className="flex w-(--sidebar-width) shrink-0 flex-col gap-3 px-2 pt-3 pb-2">
			<div className="flex h-7 items-center px-2">
				<Logo className="h-5" />
			</div>

			<ProjectSwitcher />

			<button
				type="button"
				onClick={() => openPalette(true)}
				className="flex h-8 items-center gap-2.5 rounded-md border bg-card px-2 text-[13px] text-muted-foreground outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/30"
			>
				<SearchIcon className="size-4" />
				Search or jump to…
				<Shortcut keys="mod+k" />
			</button>

			{projectId && project && (
				<nav className="flex flex-col gap-px" aria-label="Project">
					{projectNavSections.map((section) => (
						<div key={section.id} className="flex flex-col gap-px">
							<span className="mono-label px-2 pt-2 pb-1">{section.label}</span>
							{projectNavigation
								.filter((item) => item.section === section.id && !item.hidden)
								.map((item) => (
									<SidebarLink
										key={item.id}
										item={item}
										to={projectPath(projectId, item.segment)}
										badge={badges[item.id]}
									/>
								))}
						</div>
					))}
					<SidebarLink
						item={projectSettingsNavItem}
						to={projectPath(projectId, projectSettingsNavItem.segment)}
					/>
				</nav>
			)}

			<nav className="flex flex-col gap-px" aria-label="Workspace">
				<span className="mono-label px-2 pt-2 pb-1">Workspace</span>
				{workspaceNavigation.map((item) => (
					<SidebarLink key={item.id} item={item} to={item.path} end={item.id === "settings"} />
				))}
			</nav>

			<div className="mt-auto">
				<CreditMeter />
			</div>
			<div className="flex items-center justify-between px-2">
				<SyncIndicator />
				<UserMenu />
			</div>
		</aside>
	);
}
